import * as React from 'react';

import TemplateStep from 'app/pages/EmailCreateEditPage/component/TemplateStep';
import { PreviewComponent } from 'app/components/PreviewComponent/Loadable';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Unstable_Grid2';
import { Button } from '@mui/material';

interface Step1ContainerProps {
  templateState: any;
  setTemplateState: React.Dispatch<React.SetStateAction<any>>;
  error: Record<string, boolean>;
  html?: boolean;
  templateTypes?: string[];
  templateTypesLoading?: boolean;
  staticApplicableTypeOptions?: string[];
  onTypeChange?: (type: string) => void;
  onApplicableTypesChange?: (types: string[]) => void;
  htmlFile?: File | null;
  onHtmlFileSelect?: (file: File) => void;
}

export default function Step1Container({
  templateState,
  setTemplateState,
  error,
  html = true,
  templateTypes = [],
  templateTypesLoading = false,
  staticApplicableTypeOptions = [],
  onTypeChange,
  onApplicableTypesChange,
  htmlFile = null,
  onHtmlFileSelect,
}: Step1ContainerProps) {
  const [expandPreview, setExpandPreview] = React.useState(false);
  const leftColumnRef = React.useRef<HTMLDivElement>(null);
  const [leftColumnHeight, setLeftColumnHeight] = React.useState<number>(0);

  React.useEffect(() => {
    if (expandPreview) return;
    const el = leftColumnRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setLeftColumnHeight(el.offsetHeight);
    });
    ro.observe(el);
    setLeftColumnHeight(el.offsetHeight);
    return () => ro.disconnect();
  }, [expandPreview, templateState, templateTypes, templateTypesLoading]);

  return (
    <Box sx={{ flexGrow: 1, marginTop: '90px' }}>
      <Grid
        container
        justifyContent="center"
        alignItems="flex-start"
        spacing={2}
        columnGap={12}
        sx={{ marginBottom: 2 }}
      >
        <Grid
          ref={leftColumnRef}
          md={expandPreview ? 0 : 4}
          sx={{ display: expandPreview ? 'none' : 'block' }}
        >
          <TemplateStep
            templateState={templateState}
            setTemplateState={setTemplateState}
            error={error}
            html={html}
            templateTypes={templateTypes ?? ([] as string[])}
            templateTypesLoading={templateTypesLoading}
            staticApplicableTypeOptions={staticApplicableTypeOptions}
            onTypeChange={onTypeChange}
            onApplicableTypesChange={onApplicableTypesChange}
            htmlFile={htmlFile}
            onHtmlFileSelect={onHtmlFileSelect}
          />
        </Grid>
        <Grid
          md={expandPreview ? 12 : 5}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
            ...(leftColumnHeight > 0 && !expandPreview
              ? { maxHeight: leftColumnHeight }
              : expandPreview
                ? { maxHeight: 'calc(100vh - 220px)' }
                : {}),
          }}
        >
          {html && (
            <div style={{ display: 'flex', flexShrink: 0 }}>
              <Button
                variant="text"
                onClick={() => setExpandPreview(!expandPreview)}
                sx={{
                  textTransform: 'none',
                  fontSize: '16px',
                  marginRight: 'auto',
                }}
              >
                {expandPreview ? `Contract` : `Expand`}
              </Button>
            </div>
          )}
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <PreviewComponent
              compiledTemplate={templateState.html}
              showEmailContentText={false}
              fillContainer
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
